// src/components/admin/AdminUI.jsx
// Briques communes de l'espace admin : table paginée, filtres, badges, cartes.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Search, RefreshCw } from "lucide-react";

// ── Formats ──────────────────────────────────────────────
export const euros = (n) =>
    `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export const dateFr = (d, heure = false) => {
    if (!d) return "—";
    const x = new Date(String(d).replace(" ", "T"));
    if (isNaN(x)) return "—";
    return x.toLocaleDateString("fr-FR", {
        day: "2-digit", month: "short", year: "numeric",
        ...(heure ? { hour: "2-digit", minute: "2-digit" } : {}),
    });
};

// ── Badge coloré ─────────────────────────────────────────
const COULEURS = {
    vert: "bg-green-50 text-green-700 border-green-200",
    rouge: "bg-red-50 text-red-700 border-red-200",
    orange: "bg-amber-50 text-amber-700 border-amber-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
    bleu: "bg-blue-50 text-blue-700 border-blue-200",
    gris: "bg-gray-50 text-gray-600 border-gray-200",
};
export const Badge = ({ couleur = "gris", children }) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold whitespace-nowrap ${COULEURS[couleur]}`}>
        {children}
    </span>
);

// ── Carte chiffre clé ────────────────────────────────────
export const CarteKpi = ({ label, valeur, sous, couleur = "text-gray-900", icone: Icone }) => (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">{label}</p>
            {Icone && <Icone size={16} className="text-violet-500" />}
        </div>
        <p className={`text-2xl font-extrabold mt-1 ${couleur}`}>{valeur}</p>
        {sous && <p className="text-[11px] text-gray-400 mt-0.5">{sous}</p>}
    </div>
);

// ── En-tête de page ──────────────────────────────────────
export const EnTete = ({ titre, sousTitre, actions }) => (
    <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
            <h1 className="text-xl font-extrabold text-gray-900">{titre}</h1>
            {sousTitre && <p className="text-xs text-gray-500 mt-0.5">{sousTitre}</p>}
        </div>
        {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
);

// ── Recherche (attend 400 ms après la frappe) ────────────
export const Recherche = ({ valeur, onChange, placeholder = "Rechercher…" }) => {
    const [texte, setTexte] = useState(valeur || "");
    const premier = useRef(true);
    useEffect(() => {
        if (premier.current) { premier.current = false; return; }
        const t = setTimeout(() => onChange(texte), 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [texte]);
    return (
        <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
                value={texte}
                onChange={(e) => setTexte(e.target.value)}
                placeholder={placeholder}
                className="pl-8 pr-3 py-2 w-64 max-w-full rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-violet-400"
            />
        </div>
    );
};

// ── Liste déroulante de filtre ───────────────────────────
export const Filtre = ({ valeur, onChange, options }) => (
    <select
        value={valeur}
        onChange={(e) => onChange(e.target.value)}
        className="py-2 px-3 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:border-violet-400"
    >
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
);

export const BoutonActualiser = ({ onClick }) => (
    <button onClick={onClick} className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 bg-white text-xs font-semibold text-gray-600 hover:bg-gray-50">
        <RefreshCw size={13} /> Actualiser
    </button>
);

// ══════════════════════════════════════════════════════════
//  Hook : charge une liste paginée Laravel selon les filtres
//  const { page, loading, erreur, filtres, setFiltre, allerA, recharger } =
//      useListeAdmin(getUtilisateurs, { q: "", role: "" });
// ══════════════════════════════════════════════════════════
export function useListeAdmin(fetcher, filtresInitiaux = {}) {
    const [filtres, setFiltres] = useState({ page: 1, par_page: 20, ...filtresInitiaux });
    const [reponse, setReponse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [erreur, setErreur] = useState(null);

    const charger = useCallback(async () => {
        setLoading(true);
        try {
            setReponse(await fetcher(filtres));
            setErreur(null);
        } catch (e) {
            setErreur(e.message);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(filtres)]);

    useEffect(() => { charger(); }, [charger]);

    return {
        reponse,
        page: reponse?.page,
        loading,
        erreur,
        filtres,
        // changer un filtre ramène à la page 1
        setFiltre: (cle, valeur) => setFiltres((f) => ({ ...f, [cle]: valeur, page: 1 })),
        allerA: (p) => setFiltres((f) => ({ ...f, page: p })),
        setParPage: (n) => setFiltres((f) => ({ ...f, par_page: n, page: 1 })),
        recharger: charger,
    };
}

// ══════════════════════════════════════════════════════════
//  TABLE — colonnes = [{ cle, titre, rendu?(ligne), classe? }]
// ══════════════════════════════════════════════════════════
export const TableAdmin = ({ colonnes, lignes = [], loading, erreur, vide = "Aucun résultat.", ligneClasse }) => (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="bg-gray-50/80">
                    <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500">
                        {colonnes.map((c) => (
                            <th key={c.cle} className={`py-3 px-3 font-semibold whitespace-nowrap ${c.classe || ""}`}>{c.titre}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        [...Array(6)].map((_, i) => (
                            <tr key={i} className="border-t border-gray-50">
                                {colonnes.map((c) => (
                                    <td key={c.cle} className="py-3 px-3"><div className="h-3 bg-gray-100 rounded animate-pulse" /></td>
                                ))}
                            </tr>
                        ))
                    ) : erreur ? (
                        <tr><td colSpan={colonnes.length} className="py-10 text-center text-red-500">{erreur}</td></tr>
                    ) : lignes.length === 0 ? (
                        <tr><td colSpan={colonnes.length} className="py-10 text-center text-gray-400">{vide}</td></tr>
                    ) : (
                        lignes.map((l, i) => (
                            <tr key={l.id ?? i} className={`border-t border-gray-50 hover:bg-violet-50/30 align-top ${ligneClasse ? ligneClasse(l) : ""}`}>
                                {colonnes.map((c) => (
                                    <td key={c.cle} className={`py-2.5 px-3 ${c.classe || ""}`}>
                                        {c.rendu ? c.rendu(l) : (l[c.cle] ?? "—")}
                                    </td>
                                ))}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    </div>
);

// ══════════════════════════════════════════════════════════
//  PAGINATION (format Laravel paginate())
// ══════════════════════════════════════════════════════════
export const Pagination = ({ page, onPage, onParPage }) => {
    if (!page || !page.total) return null;
    const { current_page: c, last_page: der, total, from, to, per_page } = page;

    // Pages à afficher : 1 … c-1 c c+1 … der
    const nums = [];
    for (let i = 1; i <= der; i++) {
        if (i === 1 || i === der || Math.abs(i - c) <= 1) nums.push(i);
        else if (nums[nums.length - 1] !== "…") nums.push("…");
    }

    const btn = "min-w-[32px] h-8 px-2 rounded-lg text-xs font-semibold transition";

    return (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4 text-xs text-gray-500">
            <div className="flex items-center gap-2">
                <span>{from}–{to} sur {total}</span>
                {onParPage && (
                    <select value={per_page} onChange={(e) => onParPage(Number(e.target.value))}
                        className="border border-gray-200 rounded-lg px-2 py-1 bg-white">
                        {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
                    </select>
                )}
            </div>
            <div className="flex items-center gap-1">
                <button disabled={c <= 1} onClick={() => onPage(c - 1)}
                    className={`${btn} border border-gray-200 bg-white disabled:opacity-40`}><ChevronLeft size={14} className="mx-auto" /></button>
                {nums.map((n, i) => n === "…" ? (
                    <span key={`s${i}`} className="px-1">…</span>
                ) : (
                    <button key={n} onClick={() => onPage(n)}
                        className={`${btn} ${n === c ? "bg-violet-600 text-white" : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"}`}>
                        {n}
                    </button>
                ))}
                <button disabled={c >= der} onClick={() => onPage(c + 1)}
                    className={`${btn} border border-gray-200 bg-white disabled:opacity-40`}><ChevronRight size={14} className="mx-auto" /></button>
            </div>
        </div>
    );
};

// ── Petit bouton d'action dans une ligne ─────────────────
export const Action = ({ onClick, couleur = "violet", children, disabled, title }) => {
    const c = {
        violet: "text-violet-700 hover:bg-violet-50",
        rouge: "text-red-600 hover:bg-red-50",
        orange: "text-amber-700 hover:bg-amber-50",
        vert: "text-green-700 hover:bg-green-50",
    }[couleur];
    return (
        <button onClick={onClick} disabled={disabled} title={title}
            className={`px-2 py-1 rounded-lg text-xs font-semibold whitespace-nowrap disabled:opacity-30 disabled:cursor-not-allowed ${c}`}>
            {children}
        </button>
    );
};